import React, { useEffect, useState, useCallback, useRef } from 'react';
import { IonButton, IonIcon } from '@ionic/react';
import './colorPicker.css';
import moveArrows from '../../../assets/icons/arrowsMove.svg';
import { Trans } from 'react-i18next';

interface ColorPickerProps {
  onColorConfirm: (color: string) => void;
  activeElement: HTMLElement | null;
}

const ColorPicker: React.FC<ColorPickerProps> = ({ onColorConfirm, activeElement }) => {
  const [red, setRed] = useState(0);
  const [green, setGreen] = useState(0);
  const [blue, setBlue] = useState(0);
  const [position, setPosition] = useState({ top: 50, left: 50 });
  const [isDraggingPicker, setIsDraggingPicker] = useState(false);
  const [isSelectingColor, setIsSelectingColor] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const pickerRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const selectionRef = useRef<Range | null>(null);

  const hexColor = `#${red.toString(16).padStart(2, '0')}${green.toString(16).padStart(2, '0')}${blue.toString(16).padStart(2, '0')}`;

  // Draw color wheel on canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const width = canvas.width;
    const height = canvas.height;
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) / 2;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    // Draw color wheel
    for (let angle = 0; angle < 360; angle += 1) {
      const startAngle = (angle - 2) * Math.PI / 180;
      const endAngle = angle * Math.PI / 180;
      
      for (let r = 0; r < radius; r += 1) {
        const gradient = ctx.createRadialGradient(centerX, centerY, 0, centerX, centerY, radius);
        gradient.addColorStop(0, `hsl(${angle}, 100%, 50%)`);
        gradient.addColorStop(1, `hsl(${angle}, 100%, ${100 - (r / radius * 100)}%)`);
        
        ctx.beginPath();
        ctx.moveTo(centerX, centerY);
        ctx.arc(centerX, centerY, r, startAngle, endAngle);
        ctx.closePath();
        ctx.fillStyle = gradient;
        ctx.fill();
      }
    }
  }, []);

  // Handle color selection from wheel
  const handleColorSelect = useCallback((e: MouseEvent | TouchEvent) => {
    if (!isSelectingColor) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;

    let clientX: number;
    let clientY: number;

    if ('touches' in e) {
      clientX = e.touches[0].clientX;
      clientY = e.touches[0].clientY;
    } else {
      clientX = e.clientX;
      clientY = e.clientY;
    }

    const rect = canvas.getBoundingClientRect();
    const x = clientX - rect.left;
    const y = clientY - rect.top;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const pixel = ctx.getImageData(x, y, 1, 1).data;
    
    if (pixel[3] > 0) { // If not transparent
      setRed(pixel[0]);
      setGreen(pixel[1]);
      setBlue(pixel[2]);
    }
  }, [isSelectingColor]);

  const handleColorSelectStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    // Prevent triggering both color selection and dragging
    e.stopPropagation();
    setIsSelectingColor(true);
    
    // Trigger initial color selection
    const event = 'touches' in e ? e.touches[0] : e;
    handleColorSelect({
      ...e,
      clientX: event.clientX,
      clientY: event.clientY,
    } as unknown as MouseEvent);
  }, [handleColorSelect]);

  const handleColorSelectEnd = useCallback(() => {
    setIsSelectingColor(false);
  }, []);

  // Store selection when component mounts or activeElement changes
  useEffect(() => {
    const selection = window.getSelection();
    if (selection && !selection.isCollapsed) {
      selectionRef.current = selection.getRangeAt(0).cloneRange();
    }
  }, [activeElement]);

  const handleConfirm = useCallback(() => {
    if (selectionRef.current && activeElement) {
      const selection = window.getSelection();
      if (selection) {
        activeElement.focus();
        requestAnimationFrame(() => {
          selection.removeAllRanges();
          selection.addRange(selectionRef.current!);
          onColorConfirm(hexColor);
        });
      }
    } else {
      onColorConfirm(hexColor);
    }
  }, [hexColor, onColorConfirm, activeElement]);

  const handleDragStart = useCallback((e: React.MouseEvent | React.TouchEvent) => {
    // Only start dragging if we're not on the color wheel
    if ((e.target as HTMLElement).closest('canvas')) return;

    if ('touches' in e) {
      const touch = e.touches[0];
      setDragStart({
        x: touch.clientX - position.left,
        y: touch.clientY - position.top
      });
    } else {
      setDragStart({
        x: e.clientX - position.left,
        y: e.clientY - position.top
      });
    }
    setIsDraggingPicker(true);
  }, [position.left, position.top]);

  const handleDragMove = useCallback((e: MouseEvent | TouchEvent) => {
    if (!isDraggingPicker) return;

    let clientX: number;
    let clientY: number;

    if ('touches' in e) {
      const touch = e.touches[0];
      clientX = touch.clientX;
      clientY = touch.clientY;
    } else {
      clientX = (e as MouseEvent).clientX;
      clientY = (e as MouseEvent).clientY;
    }

    const newLeft = clientX - dragStart.x;
    const newTop = clientY - dragStart.y;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const pickerRect = pickerRef.current?.getBoundingClientRect();
    const pickerWidth = pickerRect?.width || 0;
    const pickerHeight = pickerRect?.height || 0;

    setPosition({
      left: Math.max(0, Math.min(newLeft, viewportWidth - pickerWidth)),
      top: Math.max(0, Math.min(newTop, viewportHeight - pickerHeight))
    });
  }, [isDraggingPicker, dragStart]);

  const handleDragEnd = useCallback(() => {
    setIsDraggingPicker(false);
  }, []);

  // Set up event listeners for color selection
  useEffect(() => {
    if (isSelectingColor) {
      document.addEventListener('mousemove', handleColorSelect);
      document.addEventListener('touchmove', handleColorSelect);
      document.addEventListener('mouseup', handleColorSelectEnd);
      document.addEventListener('touchend', handleColorSelectEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleColorSelect);
      document.removeEventListener('touchmove', handleColorSelect);
      document.removeEventListener('mouseup', handleColorSelectEnd);
      document.removeEventListener('touchend', handleColorSelectEnd);
    };
  }, [isSelectingColor, handleColorSelect, handleColorSelectEnd]);

  // Set up event listeners for picker dragging
  useEffect(() => {
    if (!isDraggingPicker) return;

    document.addEventListener('mousemove', handleDragMove);
    document.addEventListener('touchmove', handleDragMove);
    document.addEventListener('mouseup', handleDragEnd);
    document.addEventListener('touchend', handleDragEnd);

    return () => {
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('touchmove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
      document.removeEventListener('touchend', handleDragEnd);
    };
  }, [isDraggingPicker, handleDragMove, handleDragEnd]);

  return (
    <div 
      ref={pickerRef}
      className="popup"
      style={{
        top: position.top,
        left: position.left,
      }}
    >
      <div 
        className="move-handle"
        onTouchStart={handleDragStart}
        onMouseDown={handleDragStart}
        style={{
          cursor: 'move',
          padding: '0.5rem',
          display: 'flex',
          justifyContent: 'center'
        }}
      >
        <IonIcon icon={moveArrows} className="move-btn" />
      </div>
      
      {/* Color Wheel */}
      <div style={{ display: 'flex', justifyContent: 'center', margin: '1rem 0' }}>
        <canvas
          ref={canvasRef}
          width={200}
          height={200}
          onMouseDown={handleColorSelectStart}
          onTouchStart={handleColorSelectStart}
          style={{
            cursor: 'crosshair',
            borderRadius: '50%',
            touchAction: 'none'
          }}
        />
      </div>
      
      {/* Color Display */}
      <div className="mt-4 flex items-center justify-between">
        <div
          className="w-16 h-16 rounded-md border border-gray-300"
          style={{ backgroundColor: hexColor }}
        >          <span className="font-mono text-sm block mt-1">{hexColor}</span>
</div>
          <input
          className='colorPickerInput'
            type="text"
            value={hexColor}
            onChange={(e) => {
              const value = e.target.value;
              if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
                const r = parseInt(value.substr(1, 2), 16);
                const g = parseInt(value.substr(3, 2), 16);
                const b = parseInt(value.substr(5, 2), 16);
                setRed(r);
                setGreen(g);
                setBlue(b);
              }
            }}
            style={{
              padding: '0.5rem',
              border: '1px solid #ccc',
              borderRadius: '4px',
              fontFamily: 'monospace'
            }}
          />
      </div>
      
      <IonButton 
        onClick={handleConfirm}
        className="mt-4 w-full summariesColorPicker"
      >
        <Trans>Conferma colore</Trans>
      </IonButton>
    </div>
  );
};

export default ColorPicker;